/**
 * bg-options.js — Bourbon Group in-house Options Engine, PDP runtime.
 * DRAFT ONLY. Not deployed to any theme. See ../../APPLY_PLAN.md.
 *
 * Responsibilities (SPEC_bg-options-engine.md §3, §4, §6, §7, §9):
 *  - Read the Liquid-injected JSON config (no extra fetch).
 *  - Render each group's fields client-side into [data-bg-group-body].
 *  - Recursive conditional-logic evaluator (show/hide/require/disable).
 *  - Selection-state manager; writes _bg_* line-item properties into the product form.
 *  - Fee-attribute writer (_bg_fee_*, _bg_fee_label_*) consumed downstream by the Cart
 *    Transform Function — NEVER creates a product, NEVER a separate cart line.
 *  - Debounced re-evaluation (150ms) on text input; immediate on change/click.
 *  - ATC-time required-field guard (blocks submit, focuses first invalid field).
 *  - Express-wallet capture-phase guard (Buy Now / Shop Pay / Apple Pay) — same validation.
 *  - Emits `bg:configuration-complete` custom event on valid ATC (for PostHog — attach a
 *    listener in the PostHog install snippet; do NOT emit per-keystroke, see QA checklist
 *    item re: the Avis option.selected-per-keystroke bug).
 *
 * Depends on: bg-options-preview.js (loaded separately, listens for `bg:selection-changed`).
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 150;

  // Dependent per-item engraving groups -> the picker token that reveals them
  var DEP_GROUP_TOKENS = {
    'decanter-engraving': 'decanter', 'old-fashioned-glass-engraving': 'old-fashioned',
    'single-glencairn-engraving': 'glencairn', 'two-glencairn-engraving': 'two-glencairn',
    'wine-glasses-engraving': 'wine', 'champagne-flutes-engraving': 'champagne',
    'tequila-flutes-engraving': 'tequila', 'hip-flask-engraving': 'flask'
  };

  // word-overlap affinity: which picker choice does an option chain belong to
  function affinityScore(aKey, bValue) {
    var a = String(aKey).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    var b = String(bValue).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    var bs = {}; b.forEach(function (w) { bs[w] = 1; });
    var n = 0; a.forEach(function (w) { if (bs[w]) n++; });
    return n - (b.length * 0.01); // tie-break: prefer the shorter/closer choice
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function toCents(amountStr) {
    var n = parseFloat(amountStr);
    if (isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  function fromCents(cents) {
    return (cents / 100).toFixed(2);
  }

  // ---- Conditional-logic evaluator (SPEC §4.1) ----------------------------
  // Comparisons are handleized on BOTH sides (parity P1 fix 2026-07-17): configs now emit
  // slug leaves, but older on-store rules still carry label-form values ("Personal Message")
  // while selections carry slugs — normalizing at compare time keeps every vintage live.
  function bgHandle(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }
  function evalLeaf(leaf, selections) {
    var raw = selections[leaf.source_key];
    var actual = bgHandle(raw);
    switch (leaf.operator) {
      case 'equals': return actual === bgHandle(leaf.value);
      case 'not_equals': return actual !== bgHandle(leaf.value);
      case 'in': return Array.isArray(leaf.value) && leaf.value.map(bgHandle).indexOf(actual) !== -1;
      case 'not_in': return !(Array.isArray(leaf.value) && leaf.value.map(bgHandle).indexOf(actual) !== -1);
      case 'is_empty': return !raw;
      case 'is_not_empty': return !!raw;
      default: return true;
    }
  }

  function evaluateRule(rule, selections) {
    // Legacy bare-array shape (pre-07-18 liquid emitted `conditions` without its `logic`
    // sibling): the AND/OR intent is unknowable here, and guessing AND could permanently
    // hide OR-gated steps — stay inert exactly as before. The liquid emits {logic,conditions}.
    if (Array.isArray(rule)) return true;
    if (!rule || !rule.conditions) return true;
    var results = rule.conditions.map(function (c) {
      return c.type === 'leaf' ? evalLeaf(c, selections) : evaluateRule(c, selections);
    });
    return rule.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  }

  // ---- Field renderers (mirrors snippets/bg-option-field-*.liquid) --------
  var renderers = {
    text_line: function (option, groupKey) {
      var wrap = document.createElement('div');
      wrap.className = 'field bg-field bg-field--text_line';
      wrap.setAttribute('data-bg-field', '');
      wrap.setAttribute('data-key', option.key);
      wrap.setAttribute('data-input-type', 'text_line');
      var id = 'bg-input-' + groupKey + '-' + option.key;
      wrap.innerHTML =
        '<label for="' + id + '">' + option.label +
        (option.max_length ? ' <span class="bg-field__charcount" data-bg-charcount aria-hidden="true">0/' + option.max_length + '</span>' : '') +
        '</label>' +
        '<input type="text" id="' + id + '" data-bg-input maxlength="' + (option.max_length || 20) +
        '" placeholder="' + (option.placeholder || '') + '" autocomplete="off" aria-describedby="' + id + '-help">' +
        '<p id="' + id + '-help" class="bg-field__help visually-hidden">Up to ' + (option.max_length || 20) + ' characters.</p>';
      return wrap;
    },
    dropdown: function (option, groupKey) {
      var wrap = document.createElement('div');
      wrap.className = 'field bg-field bg-field--dropdown';
      wrap.setAttribute('data-bg-field', '');
      wrap.setAttribute('data-key', option.key);
      wrap.setAttribute('data-input-type', 'dropdown');
      var id = 'bg-select-' + groupKey + '-' + option.key;
      var opts = '<option value="">' + (option.placeholder || '-- Select --') + '</option>';
      (option.choices || []).forEach(function (c) {
        var feeAttr = c.fee_rule ? c.fee_rule.amount : 0;
        var feeLabel = c.fee_rule ? c.fee_rule.label : '';
        opts += '<option value="' + c.value + '" data-fee-amount="' + feeAttr + '" data-fee-label="' + feeLabel + '">' +
          c.label + (feeAttr && feeAttr != 0 ? ' (+$' + feeAttr + ')' : '') + '</option>';
      });
      wrap.innerHTML = '<label for="' + id + '">' + option.label + '</label><select id="' + id + '" data-bg-input>' + opts + '</select>';
      return wrap;
    },
    swatch_image: function (option, groupKey) {
      var wrap = document.createElement('div');
      wrap.className = 'field bg-field bg-field--swatch_image';
      wrap.setAttribute('data-bg-field', '');
      wrap.setAttribute('data-key', option.key);
      wrap.setAttribute('data-input-type', 'swatch_image');
      wrap.setAttribute('role', 'group');
      var labelId = 'bg-swatchlabel-' + groupKey + '-' + option.key;
      wrap.setAttribute('aria-labelledby', labelId);
      // Finish rows (…-finish): the legacy Avis swatch images have PRICES baked
      // into the artwork — redundant with the select-time price bubble (RD
      // 2026-07-17: "remove the price from the image swatch"). Render clean
      // label-face tiles instead; the .tag bubble still appears on selection.
      var isFinishRow = /-finish$/.test(option.key);
      // Design-type rows also drop their legacy Avis artwork (the gray/gold
      // "etched logo / custom design" art reads as silver/gold and confuses
      // the finish choice — RD 2026-07-17). Metallic looks live ONLY on the
      // Silver Fill / Gold Fill finish tiles.
      // EXCEPTION (RD 2026-07-18): the crate design-type tiles render RD's crate stock
      // photo — the label-face rule was aimed at the metallic-looking legacy bottle art.
      var isLabelRow = (isFinishRow || /-design-type$|-design_type$/.test(option.key)) && !/^wooden-gift-crate|^bottle-box-engraving/.test(option.key);
      var tiles = '';
      (option.choices || []).forEach(function (c) {
        var feeAttr = c.fee_rule ? c.fee_rule.amount : 0;
        var feeLabel = c.fee_rule ? c.fee_rule.label : '';
        var ariaLabel = c.label + (feeAttr && feeAttr != 0 ? ', add $' + feeAttr : '');
        var face = (!isLabelRow && c.swatch_image)
          ? '<img src="' + c.swatch_image + '" alt="" loading="lazy" width="44" height="44">'
          : '';
        tiles += '<button type="button" class="tile bg-tile' + (isFinishRow ? ' bg-tile--finish bg-tile--finish-' + c.value : '') +
          '" data-bg-tile data-value="' + c.value +
          '" data-fee-amount="' + feeAttr + '" data-fee-label="' + feeLabel + '" aria-pressed="false" aria-label="' + ariaLabel + '">' +
          face +
          '<span class="tface" aria-hidden="true">' + c.label + '</span>' +
          (feeAttr && feeAttr != 0 ? '<span class="tag" aria-hidden="true">+$' + feeAttr + '</span>' : '') +
          '</button>';
      });
      wrap.innerHTML =
        '<span id="' + labelId + '" class="bg-field__label">' + option.label + '</span>' +
        '<div class="tiles bg-field__tiles" data-bg-tiles>' + tiles + '</div>' +
        '<p class="bg-field__fee-sr visually-hidden" data-bg-fee-sr aria-live="polite"></p>';
      return wrap;
    },
    file_upload: function (option, groupKey) {
      var wrap = document.createElement('div');
      wrap.className = 'field bg-field bg-field--file_upload';
      wrap.setAttribute('data-bg-field', '');
      wrap.setAttribute('data-key', option.key);
      wrap.setAttribute('data-input-type', 'file_upload');
      var id = 'bg-file-' + groupKey + '-' + option.key;
      wrap.innerHTML =
        '<label for="' + id + '">' + option.label + '</label>' +
        '<div class="upload bg-field__upload">' +
        '<input type="file" id="' + id + '" name="properties[' + (option.__bg_prop_name || option.label) + ']" data-bg-file-input accept="' + (option.accepted_file_types || '.png,.jpg,.jpeg,.pdf,.svg') +
        '" aria-describedby="' + id + '-help">' +
        '<p id="' + id + '-help" class="bg-field__help">Accepted: ' + (option.accepted_file_types || '.png, .jpg, .jpeg, .pdf, .svg') + '</p>' +
        '<p class="bg-field__upload-status" data-bg-upload-status aria-live="polite"></p>' +
        '</div>' +
        '<input type="hidden" data-bg-upload-url-input value="">';
      return wrap;
    },
    heading: function (option) {
      var wrap = document.createElement('div');
      wrap.className = 'field bg-field bg-field--heading';
      wrap.setAttribute('data-bg-field', '');
      wrap.setAttribute('data-key', option.key);
      wrap.setAttribute('data-input-type', 'heading');
      wrap.setAttribute('role', 'heading');
      wrap.setAttribute('aria-level', '4');
      wrap.textContent = option.label;
      return wrap;
    }
  };

  // ---- Property-name collision guard (row 37 fix, 2026-07-30) -------------
  // The file_upload renderer names its cart property from option.LABEL (a curator-facing
  // string meant to read well on the order line) instead of the stable option KEY. Two
  // DIFFERENT option keys can carry the identical label (e.g. bottle-logo-upload vs
  // flight-handle-logo-upload vs blanton-case-logo-upload all labelled "Logo Upload...")
  // and every gift crate/gift-set that renders more than one of them puts several file
  // inputs in the SAME <form> sharing one `properties[...]` name. Repeated multipart keys
  // parse last-wins, so an empty later input silently overwrites the customer's real
  // upload. Fix: keep the FIRST option to claim a given label on its ORIGINAL name (so the
  // established engraver workflow and any in-flight order reading that name is unaffected);
  // every later option sharing that label gets a disambiguated name built from its STABLE
  // KEY, never the mutable label (match-on-stable-key-never-mutable-label).
  function bgKeyWords(key) {
    return String(key || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }
  function bgHumanizeWords(words) {
    return words.map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
  }
  function assignFileUploadPropertyNames(groups) {
    var seenLabelPrimaryKey = {}; // label -> the option key that first claimed it
    var usedNames = {};           // final property name -> true (guards a second-order collision)
    (groups || []).forEach(function (group) {
      (group.options || []).forEach(function (option) {
        if (option.input_type !== 'file_upload') return;
        var label = option.label;
        if (!Object.prototype.hasOwnProperty.call(seenLabelPrimaryKey, label)) {
          seenLabelPrimaryKey[label] = option.key;
          option.__bg_prop_name = label; // primary: name stays exactly as-is
        } else {
          var primaryWords = bgKeyWords(seenLabelPrimaryKey[label]);
          var ownWords = bgKeyWords(option.key).filter(function (w) { return primaryWords.indexOf(w) === -1; });
          var suffix = ownWords.length ? bgHumanizeWords(ownWords) : bgHumanizeWords(bgKeyWords(option.key));
          var candidate = label + ' (' + suffix + ')';
          // extremely unlikely second-order collision (two secondary keys reduce to the
          // same suffix) -- fall back to the raw key so the name still cannot collide.
          if (usedNames[candidate]) candidate = label + ' (' + option.key + ')';
          option.__bg_prop_name = candidate;
        }
        usedNames[option.__bg_prop_name] = true;
      });
    });
  }

  // ---- Engine per section ---------------------------------------------------
  function BgOptionsEngine(root) {
    this.root = root;
    this.sectionId = root.id.replace('bg-options-', '');
    this.configEl = document.getElementById('bg-options-config-' + this.sectionId);
    this.zonesEl = document.getElementById('bg-preview-zones-data-' + this.sectionId);
    this.config = this.configEl ? JSON.parse(this.configEl.textContent) : { groups: [] };
    this.zones = this.zonesEl ? JSON.parse(this.zonesEl.textContent) : [];
    assignFileUploadPropertyNames(this.config.groups); // row 37: disambiguate colliding labels
    this.selections = {}; // source_key -> current value
    this.feeState = {};   // option/choice key -> { amount, label }
    this.fieldEls = {};   // option key -> rendered wrapper element
    this.requiredKeys = []; // flattened list of {groupKey, optionKey, label}
    // LAUNCH fee bridge (RD "Bridge approved" 2026-07-17): set::option::choice -> carrier
    // variant. Fees are CHARGED as backing-product cart lines until the Cart Transform
    // swap (Phase 2); map emitted per store by config/generate-fee-bridge.js.
    var bridgeEl = document.getElementById('bg-fee-bridge-map');
    this.feeBridge = null;
    if (bridgeEl) {
      try { this.feeBridge = (JSON.parse(bridgeEl.textContent) || {}).map || null; }
      catch (e) { console.error('[bg-options] fee-bridge map unparseable', e); }
    }
    this.init();
  }

  BgOptionsEngine.prototype.init = function () {
    var self = this;
    this.config.groups.forEach(function (group) {
      var body = self.root.querySelector('[data-bg-group-body][data-group-key="' + group.key + '"]');
      if (!body) return;
      // clear noscript fallback
      body.innerHTML = '';
      group.options.forEach(function (option) {
        var renderer = renderers[option.input_type];
        if (!renderer) return;
        var el = renderer(option, group.key);
        self.fieldEls[option.key] = el;
        body.appendChild(el);
        if (group.required) self.requiredKeys.push({ groupKey: group.key, optionKey: option.key, label: option.label, option: option });
      });
    });

    // In-stack rendering (RD): per-item engraving flows live INSIDE the parent picker's
    // stack. Move each dependent group's fields into the glassware body and hide the
    // dependent <details> shell entirely; visibility is driven per-option in evaluateAll.
    // v2 (2026-07-24): bundle-awareness — products whose box already INCLUDES
    // engraveable items (gift crates: glasses, decanter) declare them via the
    // bg_options.included_items product metafield (DEP tokens, emitted into the
    // config JSON). Those items' engraving groups keep their OWN visible stack
    // (retitled "Engrave Your Included <Item>") instead of hiding behind the
    // buy-more glassware picker — the Macallan-crate incident fix.
    // PHASE-B LIVE (2026-07-24): differential pricing shipped — inc:: bridge
    // entries (delta-priced carrier variants, 48/store) verified live on all 3
    // stores before this flag flipped. Included items charge ENGRAVING ONLY.
    var INCLUDED_ITEMS_LIVE = true;
    this.__includedTokens = !INCLUDED_ITEMS_LIVE ? [] : (this.config.included_items || []).map(function (t) {
      return String(t).toLowerCase().trim();
    }).filter(Boolean);
    // option-key -> included flag (options inside dep groups whose item is in the box)
    this.__incOptionKeys = {};
    if (this.__includedTokens.length) {
      this.config.groups.forEach(function (g) {
        var tk = DEP_GROUP_TOKENS[g.key];
        if (tk && self.__includedTokens.indexOf(tk) !== -1) {
          g.options.forEach(function (o) { self.__incOptionKeys[o.key] = true; });
        }
      });
    }

    var glasswareBody = null;
    this.config.groups.forEach(function (g) {
      if (/glassware/.test(g.key)) glasswareBody = self.root.querySelector('[data-bg-group-body][data-group-key="' + g.key + '"]');
    });
    if (glasswareBody) {
      this.config.groups.forEach(function (g) {
        if (!DEP_GROUP_TOKENS[g.key]) return;
        var shell = self.root.querySelector('[data-bg-group="' + g.key + '"]');
        if (self.__includedTokens.indexOf(DEP_GROUP_TOKENS[g.key]) !== -1) {
          // included in the box: keep its own stack, retitle for clarity
          if (shell) {
            var lbl = shell.querySelector('[data-bg-group-header-label]');
            if (lbl) lbl.textContent = 'Engrave Your Included ' + g.title.replace(/\s*Engraving\s*$/i, '');
          }
          return;
        }
        g.options.forEach(function (o) {
          var el = self.fieldEls[o.key];
          if (el) glasswareBody.appendChild(el);
        });
        if (shell) shell.style.display = 'none';
      });
    }

    // annotate in-group chains (picker -> per-item option flows)
    this.config.groups.forEach(function (g) {
      if (g.key === 'bottle-engraving' || DEP_GROUP_TOKENS[g.key]) return; // sequential flows, not chains
      var picker = null;
      for (var i = 0; i < g.options.length; i++) {
        var o = g.options[i];
        if (o.input_type === 'swatch_image' && (o.choices || []).length > 1 && !o.condition_rule) { picker = o; break; }
      }
      if (!picker) return;
      var after = false, currentChoice;
      g.options.forEach(function (o) {
        if (o === picker) { after = true; return; }
        if (!after || o.input_type === 'heading') return;
        if (!o.condition_rule && (/-type$|-engraving$/.test(o.key) || /engraving.*type|design type/i.test(o.label))) {
          var best = null, bestScore = -1;
          (picker.choices || []).forEach(function (c) {
            var s = affinityScore(o.key + ' ' + o.label, c.value + ' ' + c.label);
            if (s > bestScore) { bestScore = s; best = c.value; }
          });
          currentChoice = best;
        }
        if (currentChoice !== undefined) { o.__chainChoice = currentChoice; o.__pickerKey = picker.key; }
      });
      // RD 2026-07-15: etched add-ons carry TWO text lines (Single Glencairn stays single).
      var byChain = {};
      g.options.forEach(function (o) {
        if (o.__chainChoice === undefined || o.input_type !== 'text_line') return;
        (byChain[o.__chainChoice] = byChain[o.__chainChoice] || []).push(o);
      });
      Object.keys(byChain).forEach(function (cc) {
        if (/single-glencairn/i.test(cc)) return;
        var lines = byChain[cc];
        if (lines.length !== 1) return;
        var orig = lines[0];
        var clone = {
          key: orig.key + '-line-2',
          label: (orig.label || 'Text') + ' — Line 2 (optional)',
          input_type: 'text_line', max_length: orig.max_length, placeholder: orig.placeholder,
          __chainChoice: orig.__chainChoice, __pickerKey: orig.__pickerKey
        };
        var idx = g.options.indexOf(orig);
        g.options.splice(idx + 1, 0, clone);
        var el = renderers.text_line(clone, g.key);
        self.fieldEls[clone.key] = el;
        var origEl = self.fieldEls[orig.key];
        if (origEl && origEl.parentNode) origEl.parentNode.insertBefore(el, origEl.nextSibling);
      });
    });

    // RD 2026-07-17 (dictation 07-15): bottle-engraving sub-flows scope to the chosen design
    // type — no other flow's lines may auto-open. Click Personal Message -> its text finish
    // opens; choose Etch/Silver Fill/Gold Fill -> the text lines + font selector follow.
    // Logo Upload and Custom Design flows gate the same way. Unmatched options (e.g.
    // engrave-additional-bottles) stay shared-tail and remain budget-gated.
    this.config.groups.forEach(function (g) {
      if (g.key !== 'bottle-engraving') return;
      var picker = null;
      g.options.forEach(function (o) {
        if (!picker && o.input_type === 'swatch_image' && (o.choices || []).length > 1) picker = o;
      });
      if (!picker) return;
      var FLOWS = [
        { choice: 'custom-design',    re: /custom-design|design-finish/ },
        { choice: 'logo-upload',      re: /logo/ },
        { choice: 'personal-message', re: /text-finish|text-line|font/ }
      ];
      g.options.forEach(function (o) {
        if (o === picker || o.input_type === 'heading') return;
        for (var fi = 0; fi < FLOWS.length; fi++) {
          if (FLOWS[fi].re.test(o.key)) {
            var exists = (picker.choices || []).some(function (c) { return c.value === FLOWS[fi].choice; });
            if (exists) { o.__chainChoice = FLOWS[fi].choice; o.__pickerKey = picker.key; }
            return;
          }
        }
      });
    });

    // Per-item design-type flows scope exactly like the bottle flows (launch fix 2026-07-18,
    // punch (1)+(2)): an item's logo/custom/text sub-options render and validate ONLY under
    // their OWN design type. Without this, "any finish opens the flow" revealed the font +
    // text lines inside LOGO chains and let shared-tail extras block the add. Options whose
    // key matches no flow stay shared-tail: visible after a finish, never required.
    // A sub-option can belong to SEVERAL flows (RD wood-items spec 2026-07-18: text+font
    // serve personal-message AND edge/etch-engraving on wood surfaces) — __flowChoices is a
    // SET, intersected with the choices the head actually offers.
    var FLOW_DEFS = [
      { choices: ['custom-design'], re: /custom-design|design-finish/ },
      { choices: ['logo-upload'], re: /logo/ },
      { choices: ['personal-message', 'edge-engraving', 'etch-engraving'], re: /text-finish|text-line|font|(^|-)text$/ }
    ];
    this.config.groups.forEach(function (g) {
      var head = null;
      g.options.forEach(function (o) {
        if (/-design-type$/.test(o.key)) { head = o; return; }
        if (!head || o.input_type === 'heading') return;
        // same-item guard: the option must share the head's leading token (bottle, decanter, hip...)
        if (o.key.indexOf(head.key.split('-')[0]) !== 0) return;
        for (var fi = 0; fi < FLOW_DEFS.length; fi++) {
          if (FLOW_DEFS[fi].re.test(o.key)) {
            var avail = FLOW_DEFS[fi].choices.filter(function (fc) {
              return (head.choices || []).some(function (c) { return c.value === fc; });
            });
            if (avail.length) { o.__flowChoices = avail; o.__flowPickerKey = head.key; }
            return;
          }
        }
      });
    });

    // Per-stack Add to Cart (RD): finish the form inside a stack and add without scrolling up
    Object.keys(this.fieldEls).length && this.config.groups.forEach(function (g) {
      var body = self.root.querySelector('[data-bg-group-body][data-group-key="' + g.key + '"]');
      if (!body || body.querySelector('[data-bg-stack-atc]')) return;
      var w = document.createElement('div');
      w.className = 'bg-stack-atc';
      // toggle-flow-ux-fix-20260803: this button submits the WHOLE form (every group's
      // current selections, not just this stack's) — verified in the click handler below,
      // unchanged. The bare label "Add to Cart" on every stack falsely implies a per-stack
      // scope (cited in the audit as friction point 3). Copy-only fix, zero behavior change.
      w.innerHTML = '<button type="button" data-bg-stack-atc>Add to Cart — All Selections</button>';
      body.appendChild(w);
    });

    // ---- Accordion: only ONE category stack open at a time (RD 2026-07-22) ----
    // Collapse hides only — this.selections / written form properties are untouched
    // (collapse ≠ clear). Native <details>/<summary> semantics keep keyboard operation
    // and screen-reader expanded/collapsed announcement intact. Programmatic opens
    // (focusOffending on validation) fire 'toggle' too, so the accordion holds there.
    //
    // pdp-isolation-rewire-20260804 (RD verbal 2026-08-04, from his preview of the Total
    // draft: "When you click on either one of the buttons, it should only open the drop-down
    // stack associated with it ... there should be no other categories to click on and open
    // up another drop-down stack."). Opening a stack now HIDES every other category row
    // entirely (inline style.display, not just collapse) — whether the open was triggered by
    // a CTA button (window.__bgOpenGroup, snippets/bg-options-trigger-button.liquid) or by a
    // direct click on a category's own <summary> header, since BOTH routes end in this same
    // native 'toggle' event. A "← All options" control (snippets/bg-options-body.liquid,
    // data-bg-all-options, hidden by default) is revealed the instant isolation activates and
    // restores every row (+ collapses the isolated one) on click. Display-layer only: never
    // reads or writes this.selections / this.feeState / any _bg_* property, and never touches
    // the per-stack "Add to Cart — All Selections" button built above.
    //
    // Some group shells are PERMANENTLY hidden already (line ~336 above: DEP_GROUP_TOKENS
    // per-item engraving groups whose fields were physically moved into the glassware body)
    // — isolatableGroupEls excludes those at setup time so restoring never un-hides a shell
    // that was never meant to show as its own row. bgIsolate() no-ops if asked to isolate an
    // element outside that set (e.g. a picks-summary Edit click targeting one of those merged
    // groups, a pre-existing quirk unrelated to this fix) rather than hiding every real row
    // with none left visible.
    var isolatableGroupEls = Array.prototype.slice.call(
      this.root.querySelectorAll('details.bg-options__group')
    ).filter(function (el) { return el.style.display !== 'none'; });
    var allOptionsBtn = this.root.querySelector('[data-bg-all-options]');

    function bgIsolate(openEl) {
      if (isolatableGroupEls.indexOf(openEl) === -1) return; // not a real top-level row — leave display state alone
      isolatableGroupEls.forEach(function (o) {
        o.style.display = (o === openEl) ? '' : 'none';
      });
      if (allOptionsBtn) allOptionsBtn.hidden = false;
    }

    function bgRestoreAll(collapseEl) {
      isolatableGroupEls.forEach(function (o) { o.style.display = ''; });
      if (collapseEl && collapseEl.open) collapseEl.open = false; // "collapses the current one"
      if (allOptionsBtn) allOptionsBtn.hidden = true;
    }

    if (allOptionsBtn) {
      allOptionsBtn.addEventListener('click', function () {
        var openEl = null;
        isolatableGroupEls.some(function (o) { if (o.open) { openEl = o; return true; } return false; });
        bgRestoreAll(openEl);
      });
    }

    this.root.querySelectorAll('details.bg-options__group').forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) {
          // This row just closed (native header click, or auto-closed as a sibling below).
          // If nothing else is open, isolation is over — restore every row rather than
          // leaving the panel all-hidden with nothing left to click. Idempotent: a
          // redundant call here (this row was auto-closed as part of another row's own
          // open sequence, which already re-shows everything) is a harmless no-op.
          var anyOpen = isolatableGroupEls.some(function (o) { return o.open; });
          if (!anyOpen) bgRestoreAll(null);
          return;
        }
        self.root.querySelectorAll('details.bg-options__group[open]').forEach(function (o) {
          if (o !== d) o.open = false; // open=false re-fires toggle but early-returns — no recursion
        });
        bgIsolate(d);
        // UX fix (toggle-flow-ux-fix-20260803): native <details>/<summary> toggling does
        // not auto-scroll. Opening a stack that sits far down an already-scrolled panel
        // (or one that just got auto-closed by the line above) previously left the
        // customer staring at wherever they last were with no cue the right stack opened
        // — cited in the audit as friction point (7). Smooth-scroll it into view on every
        // open, manual click or programmatic (focusOffending already does its own scroll
        // and is unaffected by this — this only adds behavior when NONE existed before).
        // SCROLL-JUMP FIXED 2026-08-20 (block pdp-uniformity-0820). RD, watching a live
        // tab: "every time that worker clicks a button it like slides it down to like the
        // bottom of the page which is weird." Ruled OUT as an automation artifact first: a
        // PURE-JS dispatchEvent('click') with no driver scroll-into-view anywhere still
        // moved the viewport +1069px on Total, +959px on Top, +908px on Rare (3/3 stores),
        // and DOM instrumentation caught THIS line firing first on every open.
        // The 2026-08-03 note above is right that native <details> does not auto-scroll,
        // but block:'start' pinned the opened stack to the TOP of the viewport. Since the
        // panel sits below the buy box, that pushed Add to Cart, the price and the
        // express-checkout buttons off-screen and pulled the footer into view — measured on
        // Total: CTA at docTop 731, post-click scrollY 811-1069, footer docTop 1715 in a
        // 2471px document, with Add to Cart confirmed NOT visible after the COMPLETE THE
        // SET click. That is a conversion defect on the highest-margin interaction on the
        // page, so the cue is kept but made minimal: scroll ONLY when the stack is actually
        // out of view, and only as far as needed (block:'nearest'). An already-visible
        // stack no longer moves the viewport at all.
        // Anchor on the group's <summary> HEADER, not the <details> itself. Measured
        // 2026-08-20: block:'nearest' silently degrades to 'start' when the element is
        // TALLER than the viewport, and 'glassware-flasks' (11 engravable sub-items) is far
        // taller than 900px — so anchoring on <details> still threw the buy box off screen
        // on COMPLETE THE SET (delta +811px, Add to Cart NOT visible) on 3/3 stores even
        // after the conditional was added. The <summary> is one row tall, so 'nearest'
        // stays minimal and the buy box survives.
        var bgAnchor = d.querySelector('summary') || d;
        var bgR = bgAnchor.getBoundingClientRect();
        var bgVh = window.innerHeight || document.documentElement.clientHeight || 0;
        if (!(bgR.top >= 0 && bgR.bottom <= bgVh)) {
          bgAnchor.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });
    });

    this.bindEvents();
    this.bindProductForm();
    this.bindExpressWallet();
    this.stampFromPrices();
    this.restampIncludedPrices();
    this.renderChoiceDescs();
    this.bindAnalytics();
    this.bindPicksSummaryEdit(); // toggle-flow-ux-fix-20260803 — see method below
    this.evaluateAll();
  };

  // ---- Phase B (2026-07-24): included-item groups display the ENGRAVING-ONLY
  // delta from the inc:: bridge entries (the choice fee_rule bakes the item cost
  // in — correct for the buy-a-glass path, wrong for glassware already in the box).
  BgOptionsEngine.prototype.restampIncludedPrices = function () {
    var self = this;
    if (!this.feeBridge || !this.__incOptionKeys) return;
    Object.keys(this.__incOptionKeys).forEach(function (key) {
      var el = self.fieldEls[key];
      if (!el) return;
      el.querySelectorAll('[data-bg-tile]').forEach(function (tile) {
        var val = tile.getAttribute('data-value');
        var tag = tile.querySelector('.tag');
        var incHit = self.feeBridge[(self.config.set_handle || '') + '::inc-' + key + '::' + val];
        if (incHit) {
          if (!tag) { tag = document.createElement('span'); tag.className = 'tag'; tile.appendChild(tag); }
          tag.textContent = '+$' + (parseFloat(incHit.a) || 0).toFixed(2);
          tile.setAttribute('data-fee-amount', incHit.a);
        } else if (/no-engraving/.test(String(val))) {
          if (!tag) { tag = document.createElement('span'); tag.className = 'tag'; tile.appendChild(tag); }
          tag.textContent = 'Included';
          tile.setAttribute('data-fee-amount', '0');
        }
      });
    });
    // design-type picker pills inside included groups: "from" = the flow's MIN inc delta
    var FLOW_SUFFIX = { 'personal-message': 'text-finish', 'edge-engraving': 'text-finish',
                        'etch-engraving': 'text-finish', 'logo-upload': 'logo-finish',
                        'custom-design': 'design-finish' };
    Object.keys(this.__incOptionKeys).forEach(function (key) {
      if (!/-design-type$/.test(key)) return;
      var el = self.fieldEls[key];
      if (!el) return;
      var groupPrefix = key.replace(/-design-type$/, '');
      el.querySelectorAll('[data-bg-tile]').forEach(function (tile) {
        var val = tile.getAttribute('data-value');
        var suf = FLOW_SUFFIX[val];
        if (!suf) return;
        var min = 0;
        var pref = (self.config.set_handle || '') + '::inc-' + groupPrefix + '-' + suf + '::';
        Object.keys(self.feeBridge).forEach(function (bk) {
          if (bk.indexOf(pref) === 0) {
            var a = parseFloat(self.feeBridge[bk].a) || 0;
            if (a > 0 && (min === 0 || a < min)) min = a;
          }
        });
        if (min > 0) {
          var tag = tile.querySelector('.tag');
          if (!tag) { tag = document.createElement('span'); tag.className = 'tag'; tile.appendChild(tag); }
          tag.textContent = 'from +$' + min.toFixed(2);
        }
      });
    });
  };

  // ---- v2 (2026-07-24): per-option description line — updates to the selected
  // choice's description (crest explanations). Renders nothing when no choice in
  // the option carries a description (schema field added v2; older configs = null).
  BgOptionsEngine.prototype.renderChoiceDescs = function () {
    var self = this;
    this.config.groups.forEach(function (g) {
      g.options.forEach(function (o) {
        if (o.input_type !== 'swatch_image') return;
        var any = (o.choices || []).some(function (c) { return c && c.description; });
        if (!any) return;
        var el = self.fieldEls[o.key];
        if (!el) return;
        var d = document.createElement('span');
        d.className = 'bg-choice-desc';
        d.setAttribute('data-bg-desc-for', o.key);
        d.setAttribute('aria-live', 'polite');
        el.appendChild(d);
      });
    });
  };

  BgOptionsEngine.prototype.updateChoiceDescs = function () {
    var self = this;
    this.root.querySelectorAll('[data-bg-desc-for]').forEach(function (d) {
      var key = d.getAttribute('data-bg-desc-for');
      var sel = self.selections[key];
      var text = '';
      if (sel) {
        self.config.groups.forEach(function (g) {
          g.options.forEach(function (o) {
            if (o.key !== key) return;
            (o.choices || []).forEach(function (c) {
              if (c.value === sel && c.description) text = c.description;
            });
          });
        });
      }
      if (d.textContent !== text) d.textContent = text;
    });
  };

  // =========================================================================
  // toggle-flow-ux-fix-20260803 (RD verbal 2026-08-03: the multi-step accordion
  // is "difficult for people to actually follow step-by-step" — a usability
  // complaint, not a functionality one). This block is READ-ONLY against
  // selections/feeState/fieldEls already computed above — it never writes a
  // _bg_* property, never touches feeState, never calls writeProperties, and
  // never changes what gets charged. It deliberately DUPLICATES the small
  // "visible + answered -> display label" read instead of refactoring
  // writeProperties' existing _bg_selection_detail block (money-adjacent code
  // another block may be mid-edit on today) — see the draft's own §3 for why.
  //
  // Two additions, both purely additive to the existing DOM:
  //   1. computeVisibleSelectionRows() + updateGroupHeaderSelections() — writes
  //      a short "what you picked" string into the [data-bg-group-header-selection]
  //      span every group's <summary> already renders but that NOTHING populated
  //      before this fix (verified: no reference to that span existed anywhere
  //      in this file pre-patch) — so a COLLAPSED stack now shows its own answer
  //      instead of going blank the moment another stack is opened.
  //   2. renderPicksSummary() — a single persistent "Your customizations"
  //      checklist, one line per engaged group, each with an Edit button that
  //      jumps back into that exact stack. Lives just above the existing fee
  //      total, i.e. directly in front of the customer's eyes right before the
  //      Add to Cart click — the same "surface it, don't bury it" principle
  //      RD named as what makes the 4-button CTA work, applied to the
  //      IN-PANEL experience instead of the panel's entry point.
  // =========================================================================
  BgOptionsEngine.prototype.computeVisibleSelectionRows = function () {
    var self = this;
    var rows = [];
    this.config.groups.forEach(function (group) {
      group.options.forEach(function (o) {
        if (o.input_type === 'heading') return;
        var el = self.fieldEls[o.key];
        if (el && el.hidden) return; // mirrors writeProperties' own rule: a hidden/inactive field's value isn't "picked" yet
        var v = self.selections[o.key];
        if (v == null || v === '') return;
        var display = String(v);
        (o.choices || []).forEach(function (c) { if (c.value === v) display = c.label; });
        rows.push({ groupKey: group.key, groupTitle: group.title, optionLabel: o.label || o.key, display: display });
      });
    });
    return rows;
  };

  BgOptionsEngine.prototype.updateGroupHeaderSelections = function () {
    var self = this;
    var byGroup = {};
    this.computeVisibleSelectionRows().forEach(function (r) {
      (byGroup[r.groupKey] = byGroup[r.groupKey] || []).push(r.display);
    });
    this.config.groups.forEach(function (group) {
      var headerSel = self.root.querySelector('[data-bg-group="' + group.key + '"] [data-bg-group-header-selection]');
      if (!headerSel) return;
      var parts = byGroup[group.key] || [];
      var text = parts.length ? parts.slice(0, 3).join(', ') + (parts.length > 3 ? '…' : '') : '';
      if (headerSel.textContent !== text) headerSel.textContent = text;
    });
  };

  BgOptionsEngine.prototype.renderPicksSummary = function () {
    var self = this;
    var box = this.root.querySelector('[data-bg-picks-summary]');
    if (!box) return; // liquid shell not present on this theme copy yet — no-op, never throws
    var byGroup = {}, order = [];
    this.computeVisibleSelectionRows().forEach(function (r) {
      if (!byGroup[r.groupKey]) { byGroup[r.groupKey] = []; order.push(r.groupKey); }
      byGroup[r.groupKey].push(r.display);
    });
    if (!order.length) { box.innerHTML = ''; box.hidden = true; return; }
    box.hidden = false;
    var html = '<p class="bg-options__picks-title">Your customizations</p><ul class="bg-options__picks-list">';
    order.forEach(function (gk) {
      var group = null;
      for (var i = 0; i < self.config.groups.length; i++) { if (self.config.groups[i].key === gk) { group = self.config.groups[i]; break; } }
      var title = group ? group.title : gk;
      var esc = function (s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); };
      html += '<li class="bg-options__picks-row">' +
        '<span class="bg-options__picks-row-label">' + esc(title) + ':</span>' +
        '<span class="bg-options__picks-row-value">' + esc(byGroup[gk].slice(0, 3).join(', ')) + '</span>' +
        '<button type="button" class="bg-options__picks-edit" data-bg-picks-edit data-group-key="' + esc(gk) +
        '" aria-label="Edit ' + esc(title) + ' selection">Edit</button>' +
        '</li>';
    });
    html += '</ul>';
    if (box.innerHTML !== html) box.innerHTML = html;
  };

  // Fallback opener — only used if the CTA-4button draft's window.__bgOpenGroup
  // (snippets/bg-options-trigger-button.liquid) is not present on this theme
  // copy, so this draft's Edit links work standalone whether or not that draft
  // has also landed. Never overwrites window.__bgOpenGroup if it already exists.
  function bgOpenGroupFallback(groupKey, root) {
    root.classList.add('bg-open');
    root.querySelectorAll('details.bg-options__group').forEach(function (d) {
      d.open = (d.getAttribute('data-bg-group') === groupKey);
    });
    var target = root.querySelector('[data-bg-group="' + groupKey + '"]');
    // Same scroll-jump fix as the toggle listener above (block pdp-uniformity-0820,
    // 2026-08-20) — keep this fallback's behavior identical to the primary path so the
    // defect cannot come back through whichever opener happens to be in play.
    if (target) {
      var fbAnchor = target.querySelector('summary') || target;
      var fbR = fbAnchor.getBoundingClientRect();
      var fbVh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (!(fbR.top >= 0 && fbR.bottom <= fbVh)) {
        fbAnchor.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  }

  BgOptionsEngine.prototype.bindPicksSummaryEdit = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var editBtn = e.target.closest('[data-bg-picks-edit]');
      if (!editBtn) return;
      var gk = editBtn.getAttribute('data-group-key');
      if (typeof window.__bgOpenGroup === 'function') window.__bgOpenGroup(gk, editBtn);
      else bgOpenGroupFallback(gk, self.root);
    });
  };

  // ---- v2 (2026-07-24): funnel instrumentation — PostHog when present, no-op
  // otherwise. Panel toggle + configuration-complete only (never per-keystroke,
  // per the header warning re: the Avis option.selected bug).
  BgOptionsEngine.prototype.bindAnalytics = function () {
    var self = this;
    function ph(ev, props) {
      try { if (window.posthog && window.posthog.capture) window.posthog.capture(ev, props || {}); } catch (e) {}
    }
    document.querySelectorAll('[data-bg-options-trigger] button').forEach(function (btn) {
      btn.addEventListener('click', function () {
        ph('bg_options_panel_toggle', {
          open: self.root.classList.contains('bg-open'),
          product_id: self.root.getAttribute('data-product-id'),
          set_handle: self.root.getAttribute('data-set-handle')
        });
      });
    });
    this.root.addEventListener('bg:configuration-complete', function (e) {
      ph('bg_options_configuration_complete', {
        product_id: self.root.getAttribute('data-product-id'),
        set_handle: self.root.getAttribute('data-set-handle')
      });
    });
  };

  BgOptionsEngine.prototype.bindEvents = function () {
    var self = this;
    var debouncedEval = debounce(function () { self.evaluateAll(); self.writeProperties(); }, DEBOUNCE_MS);

    this.root.addEventListener('input', function (e) {
      var input = e.target.closest('[data-bg-input]');
      if (!input) return;
      var field = input.closest('[data-bg-field]');
      var key = field.getAttribute('data-key');
      self.selections[key] = input.value;
      debouncedEval();
    });

    this.root.addEventListener('change', function (e) {
      var select = e.target.closest('select[data-bg-input]');
      if (select) {
        var field = select.closest('[data-bg-field]');
        var key = field.getAttribute('data-key');
        self.selections[key] = select.value;
        var chosen = select.options[select.selectedIndex];
        self.feeState[key] = {
          amount: chosen ? parseFloat(chosen.getAttribute('data-fee-amount')) || 0 : 0,
          label: chosen ? chosen.getAttribute('data-fee-label') || '' : ''
        };
        self.evaluateAll();
        self.writeProperties();
        return;
      }
      var fileInput = e.target.closest('[data-bg-file-input]');
      if (fileInput) {
        self.handleFileSelect(fileInput);
      }
    });

    this.root.addEventListener('click', function (e) {
      var stackAtc = e.target.closest('[data-bg-stack-atc]');
      if (stackAtc) {
        self.writeProperties();
        // #44210: the panel's own buy button is the likeliest path for an engraving
        // customer, and it had no upload-awareness whatsoever.
        var upStack = self.uploadFlowUnsatisfied();
        if (upStack) { self.focusOffending(upStack); alert('Please attach your artwork file before continuing -- the logo upload option is selected but no file has been added.'); return; }
        var res = self.validateRequiredSelections();
        // focusOffending (not bare scrollIntoView): it opens the offender's <details> too —
        // a bare scroll to a field inside a closed stack is invisible and reads as a dead
        // button (the exact symptom of the 07-17 upload-ATC "no-op", launch fix 2026-07-18).
        if (res && res.valid === false && res.el) { self.focusOffending(res); return; }
        // POST the main form directly: programmatic clicks on the theme's add
        // button never dispatch submit on Enterprise (apo-exclude era handler
        // swallows the activation — found 2026-07-17), and fetch keeps the
        // full properties payload deterministic.
        if (self.productForm) {
          stackAtc.disabled = true;
          stackAtc.textContent = 'Adding…';
          // Shopify's AJAX cart endpoint rejects file uploads — lines carrying
          // a file must go through a NATIVE multipart form post (which also
          // bypasses the theme's ajax interception and navigates to /cart).
          var liveForm = document.querySelector('form.js-product-form-main') || self.productForm;
          self.productForm = liveForm;
          if (!liveForm.getAttribute('id')) liveForm.setAttribute('id', 'bg-main-product-form');
          var hasFile = false;
          self.root.querySelectorAll('[data-bg-file-input]').forEach(function (fi) {
            fi.setAttribute('form', liveForm.getAttribute('id'));
            if (fi.files && fi.files.length) hasFile = true;
          });
          // LAUNCH fee bridge: charge fees by adding carrier variants as their own cart
          // lines (best-effort here — the cart-page sweeper reconciles from the
          // _bg_fee_carriers property, so a failed add here self-heals at the cart).
          var carriersInput = liveForm.querySelector('input[name="properties[_bg_fee_carriers]"]');
          var carrierIds = (carriersInput && carriersInput.value) ? carriersInput.value.split(',') : [];
          // D215 (2026-08-03, RD ballot decision 215 -- FLEET-WIDE, overriding the narrower
          // own-item-PDP-only recommendation carried in D211's draft): every fee-carrier
          // cart line's quantity must bind to the PDP's own quantity selector, never a
          // hardcoded 1. Read from the SAME live form node the main product line's
          // FormData submission already uses (liveForm === self.productForm at this point),
          // so the main line and every carrier line can never disagree by construction.
          // This is not a new semantic: it matches the invariant the cart-page sweeper
          // (snippets/bg-fee-carrier-sweeper.liquid, live fleet-wide since 2026-07-18) has
          // already been enforcing on EVERY /cart view for EVERY fee carrier --
          //   qty(carrier) == main-line qty x occurrences in that line's _bg_fee_carriers.
          // This patch closes the gap AT THE SOURCE instead of relying solely on the
          // sweeper's async cart-page reconciliation, which cannot run before an
          // express-checkout-free native submit completes, leaving a narrow window where a
          // fast client could read (or, in a race, check out against) the stale qty-1 line.
          var bgQtyEl = liveForm.querySelector('[name="quantity"]');
          var bgQty = 1;
          if (bgQtyEl) {
            var bgQtyVal = parseInt(bgQtyEl.value, 10);
            if (bgQtyVal > 0 && bgQtyVal < 1000) bgQty = bgQtyVal;
          }
          var addCarriers = function () {
            if (!carrierIds.length) return Promise.resolve();
            return fetch('/cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: carrierIds.map(function (v) {
                return { id: parseInt(v, 10), quantity: bgQty, properties: { _bg_carrier: '1' } };
              }) })
            }).then(function (r) { if (!r.ok) console.error('[bg-options] fee-carrier add failed', r.status); })
              .catch(function (e) { console.error('[bg-options] fee-carrier add failed', e); });
          };
          if (hasFile) {
            self.writeProperties();
            // carriers first (no files involved), then the native multipart submit
            addCarriers().then(function () {
              HTMLFormElement.prototype.submit.call(liveForm);
            });
            return;
          }
          fetch('/cart/add.js', { method: 'POST', body: new FormData(self.productForm) })
            .then(function (r) { if (!r.ok) throw new Error('add failed ' + r.status); return r.json(); })
            .then(function () { return addCarriers(); })
            .then(function () { window.location.href = '/cart'; })
            .catch(function () {
              stackAtc.disabled = false;
              stackAtc.textContent = 'Add to Cart';
              alert('Sorry — the item could not be added. Please try again.');
            });
        }
        return;
      }
      var tile = e.target.closest('[data-bg-tile]');
      if (!tile) return;
      var field = tile.closest('[data-bg-field]');
      var key = field.getAttribute('data-key');
      var group = tile.closest('[data-bg-tiles]'); // FIX: closest() walks UP; tiles container is the tile's ancestor
      var wasPressed = tile.getAttribute('aria-pressed') === 'true';
      // single-select swatch: deselect siblings
      group.querySelectorAll('[data-bg-tile]').forEach(function (t) { t.setAttribute('aria-pressed', 'false'); });
      if (wasPressed) {
        // RD 2026-07-15: clicking the selected tile UNSELECTS it — its flow collapses
        delete self.selections[key];
        delete self.feeState[key];
        self.evaluateAll();
        self.writeProperties();
        return;
      }
      tile.setAttribute('aria-pressed', 'true');
      self.selections[key] = tile.getAttribute('data-value');
      var amount = parseFloat(tile.getAttribute('data-fee-amount')) || 0;
      var label = tile.getAttribute('data-fee-label') || '';
      // Phase B: item already in the box -> charge the ENGRAVING-ONLY delta
      // (inc:: bridge entry); no-engraving on an included item is free.
      if (self.__incOptionKeys && self.__incOptionKeys[key]) {
        var incHit = self.feeBridge && self.feeBridge[(self.config.set_handle || '') + '::inc-' + key + '::' + self.selections[key]];
        if (incHit) { amount = parseFloat(incHit.a) || 0; label = (incHit.l || label); }
        else if (/no-engraving/.test(String(self.selections[key]))) { amount = 0; }
      }
      self.feeState[key] = { amount: amount, label: label };
      var srEl = field.querySelector('[data-bg-fee-sr]');
      if (srEl) srEl.textContent = amount ? (tile.getAttribute('aria-label')) : '';
      self.evaluateAll();
      self.writeProperties();
    });
  };

  BgOptionsEngine.prototype.handleFileSelect = function (fileInput) {
    var field = fileInput.closest('[data-bg-field]');
    var key = field.getAttribute('data-key');
    var file = fileInput.files && fileInput.files[0];
    var status = field.querySelector('[data-bg-upload-status]');
    if (!file) { status.textContent = ''; return; }
    this.selections[key] = file.name;
    status.textContent = 'Selected: ' + file.name + ' (will upload on Add to Cart)';
    // Local preview only — no network round trip yet (bg-options-preview.js listens for this)
    var objectUrl = URL.createObjectURL(file);
    this.root.dispatchEvent(new CustomEvent('bg:file-selected', { detail: { key: key, objectUrl: objectUrl }, bubbles: true }));
    this.evaluateAll();
  };

  // ---- Conditional show/hide/require/disable per group + per option --------
  BgOptionsEngine.prototype.evaluateAll = function () {
    var self = this;
    this.__evalPass = (this.__evalPass || 0) + 1;
    // Accessory-dependent groups (Avis parity, RD 2026-07-15): an "<Item> Engraving" group
    // renders only AFTER that item is selected in the accessory picker. Matching is by
    // handleized token against all current selection values.
    var selBlob = Object.keys(self.selections).map(function(k){return String(self.selections[k]);}).join(' ').toLowerCase();
    // v2: items already included in the product's box count as "selected" — their
    // engraving groups are reachable without buying a duplicate from the picker.
    if (self.__includedTokens && self.__includedTokens.length) selBlob += ' ' + self.__includedTokens.join(' ');
    var DEP_TOKENS = DEP_GROUP_TOKENS;
    // D215 companion fix (defect 2 from the D211 draft): DEP_TOKENS values collide as raw
    // substrings ('two-glencairn' contains 'glencairn'), so a plain selBlob.indexOf(depToken)
    // let a two-glencairn selection also reveal the single-glencairn engraving group. A
    // simple space- or hyphen-boundary test is NOT sufficient either -- proven by test before
    // ship, not assumed -- because the LONGER token is itself hyphen-bounded inside the
    // selection value ('-glencairn-' is a literal substring of '-two-glencairn-glasses-').
    // The correct resolution (and the one already established elsewhere in this file for the
    // identical collision, see the tile "from"-price longest-match block below, 2026-07-24
    // comment "two-glencairn beats glencairn"): for each selected value, the LONGEST
    // hyphen-bounded DEP_TOKENS value wins; only that winner counts as "present" for
    // visibility. Verified against known positive/negative pairs, not guessed.
    var __bgSelWords = selBlob.split(' ').filter(Boolean);
    var __bgMatchedDepTokens = {};
    __bgSelWords.forEach(function (w) {
      var padded = '-' + w + '-';
      var best = null, bestLen = 0;
      Object.keys(DEP_TOKENS).forEach(function (gk) {
        var t = DEP_TOKENS[gk];
        if (padded.indexOf('-' + t + '-') !== -1 && t.length > bestLen) { best = t; bestLen = t.length; }
      });
      if (best) __bgMatchedDepTokens[best] = true;
    });
    this.config.groups.forEach(function (group) {
      var groupVisible = evaluateRule(group.condition_rule, self.selections);
      var depToken = DEP_TOKENS[group.key];
      if (depToken && !__bgMatchedDepTokens[depToken]) groupVisible = false;
      var groupBody = self.root.querySelector('[data-bg-group-body][data-group-key="' + group.key + '"]');
      var groupDetails = groupBody ? groupBody.closest('[data-bg-group]') : null;
      if (groupDetails) groupDetails.hidden = !groupVisible;

      // Progressive disclosure (RD 2026-07-15, Avis-parity): inside a group, options reveal
      // step-by-step — everything already answered stays visible, plus exactly the NEXT
      // unanswered option. Rule-hidden options never reveal. Headings are always shown with
      // their following step.
      var revealBudgetSpent = false;
      // Step-gating applies ONLY to engraving flows (bottle + per-item). Product PICKER groups
      // (glassware set, cases, chillers, cigar accessories, crate) always show all their
      // tiles — Avis parity: you can always see the products; their engraving steps gate.
      var groupIsGated = group.key === 'bottle-engraving' || !!DEP_TOKENS[group.key];
      group.options.forEach(function (option) {
        var el = self.fieldEls[option.key];
        if (!el) return;
        // Phase B: included-item options bypass their buy-the-item condition_rule —
        // the box already contains the item; step-gating below still sequences them.
        var visible = groupVisible && ((self.__incOptionKeys && self.__incOptionKeys[option.key])
          ? true : evaluateRule(option.condition_rule, self.selections));
        // In-group item chains (cigar accessories etc.): a chain HEAD is an option whose key
        // ends in '-type'; the chain (head + following options until the next head) shows only
        // when its best-affinity picker choice is the current picker selection.
        if (visible && option.__chainChoice !== undefined) {
          var pickSel = self.selections[option.__pickerKey];
          if (!pickSel || pickSel !== option.__chainChoice) visible = false;
          else {
            // RD dictation: after ANY finish choice (Etch, Silver Fill, or Gold Fill) the
            // rest of the flow reveals together — text line(s) AND the font selector.
            var chainFinished = false;
            group.options.forEach(function (o2) {
              if (o2.__chainChoice === option.__chainChoice) {
                var v2 = self.selections[o2.key];
                if (v2 && /etch|silver|gold/i.test(String(v2))) chainFinished = true;
              }
            });
            if (chainFinished) { /* finish chosen: full flow visible — text line(s) AND font always shown */ }
            else {
            var budgetKey = group.key + '::' + option.__chainChoice;
            self.__chainBudget = self.__chainBudget || {};
            if (self.__chainBudgetPass !== self.__evalPass) { self.__chainBudget = {}; self.__chainBudgetPass = self.__evalPass; }
            var answeredC = self.selections[option.key] != null && self.selections[option.key] !== '';
            if (!answeredC) {
              if (self.__chainBudget[budgetKey]) visible = false;
              else self.__chainBudget[budgetKey] = true;
            }
            }
          }
        }
        // Flow scoping (launch fix 2026-07-18): a design-type sub-option only renders when
        // ITS design type (one of its flow set) is the current selection.
        if (visible && option.__flowChoices) {
          var flowSel = self.selections[option.__flowPickerKey];
          if (!flowSel || option.__flowChoices.indexOf(flowSel) === -1) visible = false;
        }
        if (groupIsGated && visible && option.input_type !== 'heading') {
          // RD dictation: ANY finish choice (Etch/Silver Fill/Gold Fill) opens the rest of
          // the flow (text lines + font). Chain-aware: only selections belonging to the
          // CURRENT design-type flow (or shared options) count — a stale finish left over
          // from a previously-picked design type never opens another flow's lines.
          var groupFinished = false;
          group.options.forEach(function (og) {
            if (og.__chainChoice !== undefined && self.selections[og.__pickerKey] !== og.__chainChoice) return;
            var vg = self.selections[og.key];
            if (vg && /etch|silver|gold/i.test(String(vg))) groupFinished = true;
          });
          if (!groupFinished) {
            var answered = self.selections[option.key] != null && self.selections[option.key] !== '';
            if (!answered) {
              if (revealBudgetSpent) visible = false;
              else revealBudgetSpent = true; // this is the one "next step" we show
            }
          } // finish chosen: full flow visible — all text lines AND the font selector
        }
        el.hidden = !visible;
        el.setAttribute('aria-hidden', String(!visible));
        var interactive = el.querySelectorAll('input, select, button, textarea');
        interactive.forEach(function (node) {
          if (!visible) {
            node.setAttribute('tabindex', '-1');
            node.disabled = true;
          } else {
            node.removeAttribute('tabindex');
            node.disabled = false;
          }
        });
      });
    });
    // Summary titling (RD): eyebrow = internal group name, main line = original Avis option title
    this.config.groups.forEach(function (group) {
      var head = self.root.querySelector('[data-bg-group="' + group.key + '"] [data-bg-group-header-label]');
      if (!head || head.getAttribute('data-bg-titled')) return;
      // Single line only (RD): the original Avis section title. For picker groups that carry
      // several options, that's the swatch option with the MOST choices (the product picker),
      // not whichever option happens to render first.
      var candidates = (group.options || []).filter(function (o) { return o.input_type !== 'heading'; });
      // Title = the FIRST swatch option offering a real choice (>1). Skips single-item lead-ins
      // (flight handle) without jumping to later, bigger swatches (custom designs).
      var mainOpt = null;
      for (var ci = 0; ci < candidates.length; ci++) {
        var o = candidates[ci];
        if (o.input_type === 'swatch_image' && (o.choices || []).length > 1 && !o.condition_rule) { mainOpt = o; break; }
      }
      if (!mainOpt) mainOpt = candidates[0];
      var mainTitle = mainOpt ? mainOpt.label : group.title;
      head.innerHTML = '<span class="bg-options__group-main">' + mainTitle + '</span>';
      head.setAttribute('data-bg-titled', '1');
    });
    this.updateFeeSummary();
    this.updateChoiceDescs(); // v2: selected-crest description line follows every evaluation
    this.updateGroupHeaderSelections(); // toggle-flow-ux-fix-20260803
    this.renderPicksSummary();          // toggle-flow-ux-fix-20260803
    this.root.dispatchEvent(new CustomEvent('bg:selection-changed', {
      detail: { selections: Object.assign({}, this.selections), feeState: Object.assign({}, this.feeState), zones: this.zones },
      bubbles: true
    }));
  };

  BgOptionsEngine.prototype.updateFeeSummary = function () {
    var self = this;
    var totalCents = 0;
    Object.keys(this.feeState).forEach(function (key) {
      // only count fees for currently-visible fields (a hidden/inactive option's fee doesn't apply)
      var el = self.fieldEls[key];
      if (el && el.hidden) return;
      totalCents += toCents(self.feeState[key].amount);
    });
    var totalEl = this.root.querySelector('[data-bg-fee-total]');
    if (totalEl) totalEl.textContent = '$' + fromCents(totalCents);
  };

  // ---- Required-field validation (ATC guard + express-wallet guard) --------
  BgOptionsEngine.prototype.groupEngaged = function (groupKey) {
    for (var gi = 0; gi < this.config.groups.length; gi++) {
      var g = this.config.groups[gi];
      if (g.key !== groupKey) continue;
      for (var oi = 0; oi < g.options.length; oi++) {
        var v = this.selections[g.options[oi].key];
        if (v !== undefined && v !== null && v !== '') return true;
      }
      return false;
    }
    return false;
  };

  // Does the LIVE product form currently carry real written _bg_ selections?
  // (order #44210, 2026-09-01: writeProperties() plants hidden data-bg-written inputs as
  // soon as a flow is picked, and they SURVIVE a panel collapse -- so "panel closed"
  // alone was never a safe proxy for "plain purchase".)
  BgOptionsEngine.prototype.hasWrittenSelections = function () {
    var f = document.querySelector('form.js-product-form-main') || this.productForm;
    if (!f) return false;
    var nodes = f.querySelectorAll('input[data-bg-written]');
    for (var i = 0; i < nodes.length; i++) {
      var nm = nodes[i].getAttribute('name') || '';
      if (nm.indexOf('_bg_option_set_handle') !== -1) continue;
      if (nm.indexOf('_bg_selection_detail') !== -1) continue;
      if ((nodes[i].value || '') !== '') return true;
    }
    return false;
  };

  // Is an upload design-type currently written into the live product form?
  // Separate from uploadFlowUnsatisfied(): "engaged" is true even once a file IS attached,
  // because the AJAX cart drops files entirely -- an engaged flow must be routed to a
  // native multipart post and must never leave via an express wallet.
  BgOptionsEngine.prototype.uploadFlowEngaged = function () {
    var f = document.querySelector('form.js-product-form-main') || this.productForm;
    if (!f) return false;
    var written = f.querySelectorAll('input[data-bg-written]');
    for (var i = 0; i < written.length; i++) {
      var nm = written[i].getAttribute('name') || '';
      if (nm.indexOf('_bg_') === -1) continue;
      if (/upload/i.test(written[i].value || '')) return true;
    }
    return false;
  };

  // Upload-flow guard (order #44210): an engraving flow whose design-type says "upload"
  // must not reach /cart/add with an empty file input. Independent of panel state, so
  // native submit and express wallets are both covered. Returns null when satisfied.
  BgOptionsEngine.prototype.uploadFlowUnsatisfied = function () {
    var f = document.querySelector('form.js-product-form-main') || this.productForm;
    if (!f || !this.root) return null;
    var wantsUpload = false;
    var written = f.querySelectorAll('input[data-bg-written]');
    for (var i = 0; i < written.length; i++) {
      var nm = written[i].getAttribute('name') || '';
      if (nm.indexOf('_bg_') === -1) continue;
      if (/upload/i.test(written[i].value || '')) { wantsUpload = true; break; }
    }
    if (!wantsUpload) return null;
    var inputs = this.root.querySelectorAll('[data-bg-file-input]');
    var firstActive = null;
    var sawActive = false;
    for (var j = 0; j < inputs.length; j++) {
      var inp = inputs[j];
      var host = inp.closest('[data-bg-field]') || inp.parentElement;
      if (host && host.hidden) continue;
      sawActive = true;
      if (!firstActive) firstActive = host || inp;
      if (inp.files && inp.files.length > 0) return null; // a file IS attached
    }
    if (!sawActive) return null; // no active upload field on screen -- never over-block
    return { valid: false, upload: true, el: firstActive || this.root };
  };

  BgOptionsEngine.prototype.validateRequiredSelections = function () {
    // Panel-closed = plain purchase, no customization: never block ATC/express (RD 2026-07-15).
    // Scoped 2026-09-01: only a TRUE plain purchase bypasses. If written _bg_ properties are
    // already riding in the form, the guard must still run (order #44210 root cause).
    if (!this.root.classList.contains('bg-open') && !this.hasWrittenSelections()) return { valid: true };
    for (var i = 0; i < this.requiredKeys.length; i++) {
      var req = this.requiredKeys[i];
      var el = this.fieldEls[req.optionKey];
      if (!el || el.hidden) continue; // not applicable if conditionally hidden
      // group.required blankets EVERY option in the group (the generator collapsed Avis's
      // per-option required to group level) — so requiredness is scoped here instead
      // (launch fix 2026-07-18, punch (1)): explicitly-optional steps never block, and in
      // gated engraving groups only the design-type head and the ACTIVE flow's steps count.
      // Shared-tail upsells (engrave-additional-bottles) are offers, not steps.
      if (/\boptional\b/i.test(req.label || '')) continue;
      // second engraving line is optional BY DESIGN (RD's Line-2 clone directive labels it
      // so; the migrated originals — "Bottle Text Line 2" — carry no marker but mean the same)
      if (/(^|-)line-2$/.test(req.optionKey)) continue;
      // Engagement gate (launch fix 2026-07-18): a stack the shopper never touched carries
      // no properties into the add, so it has nothing to require — an untouched required
      // group (wooden gift crate) must never block another stack's Add to Cart.
      if (!this.groupEngaged(req.groupKey)) continue;
      var opt = req.option || {};
      var gated = req.groupKey === 'bottle-engraving' || !!DEP_GROUP_TOKENS[req.groupKey];
      if (gated) {
        var inFlow = opt.__chainChoice !== undefined || opt.__flowChoices !== undefined;
        if (!inFlow && !/-design-type$/.test(req.optionKey)) continue;
        if (opt.__chainChoice !== undefined && this.selections[opt.__pickerKey] !== opt.__chainChoice) continue;
        if (opt.__flowChoices && opt.__flowChoices.indexOf(this.selections[opt.__flowPickerKey]) === -1) continue;
      }
      var value = this.selections[req.optionKey];
      if (!value) {
        return { valid: false, offendingKey: req.optionKey, groupKey: req.groupKey, el: el };
      }
    }
    return { valid: true };
  };

  BgOptionsEngine.prototype.focusOffending = function (result) {
    var details = result.el.closest('details.bg-options__group');
    if (details) details.open = true;
    result.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var focusable = result.el.querySelector('input, select, button');
    if (focusable) focusable.focus();
  };

  // ---- Line-item property writer (writes into the product form) ------------
  BgOptionsEngine.prototype.writeProperties = function () {
    // the theme re-renders the product form (web component) — always write
    // into the LIVE node, never a cached/detached one (found 2026-07-18)
    var live = document.querySelector('form.js-product-form-main');
    if (live) this.productForm = live;
    if (!this.productForm) return;
    var self = this;
    // remove previously-written bg properties before re-writing (selection may have changed)
    this.productForm.querySelectorAll('input[data-bg-written]').forEach(function (n) { n.remove(); });

    function addHidden(name, value) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      input.value = value;
      input.setAttribute('data-bg-written', '');
      self.productForm.appendChild(input);
    }

    addHidden('_bg_option_set_handle', this.config.set_handle || '');
    Object.keys(this.selections).forEach(function (key) {
      var el = self.fieldEls[key];
      if (el && el.hidden) return; // don't persist a hidden/inactive field's value
      addHidden('_bg_' + key, self.selections[key]);
    });
    // ---- Cart second-look detail (RD 2026-07-22) ----------------------------
    // One human-readable property: "Label: Value ¦ Label: Value". Labels/choice-
    // labels resolved from config (typed text verbatim; slugs become display
    // labels). Underscore prefix keeps it out of the theme's generic property
    // loop; rendered only by bg-options-cart-breakdown.liquid. ' ¦ ' delimits
    // rows, ': ' separates label/value — both stripped from labels, delimiter
    // stripped from values, so parsing stays unambiguous.
    var detailRows = [];
    this.config.groups.forEach(function (g) {
      g.options.forEach(function (o) {
        if (o.input_type === 'heading') return;
        var v = self.selections[o.key];
        if (v == null || v === '') return;
        var elD = self.fieldEls[o.key];
        if (elD && elD.hidden) return; // mirror the _bg_* rule: hidden fields never persist
        var display = String(v);
        (o.choices || []).forEach(function (c) { if (c.value === v) display = c.label; });
        detailRows.push(
          String(o.label || o.key).replace(/[¦:]/g, ' ').trim() + ': ' +
          display.replace(/¦/g, ' ')
        );
      });
    });
    if (detailRows.length) addHidden('_bg_selection_detail', detailRows.join(' ¦ '));
    // Single canonical fee attribute: the Cart Transform input query can only
    // read NAMED attribute keys (no enumeration), so all fees ride in one
    // _bg_fees JSON array. _bg_fee_summary is the human-readable mirror for
    // admin order detail / packing slips.
    var feeEntries = [];
    var feeKeys = [];
    Object.keys(this.feeState).forEach(function (key) {
      var el = self.fieldEls[key];
      if (el && el.hidden) return;
      var fee = self.feeState[key];
      if (fee && fee.amount) {
        feeEntries.push({ l: fee.label || 'Customization fee', a: fee.amount.toFixed(2) });
        feeKeys.push(key);
      }
    });
    // resolve each visible fee to its carrier variant; the id list rides on the main line
    // so the cart-page sweeper can reconcile carriers with NO map access (self-contained)
    var carrierIds = [];
    if (this.feeBridge && feeKeys.length) {
      feeKeys.forEach(function (key) {
        // Phase B: included-item selections resolve to the delta-priced carrier first
        var hit = null;
        if (self.__incOptionKeys && self.__incOptionKeys[key]) {
          hit = self.feeBridge[(self.config.set_handle || '') + '::inc-' + key + '::' + self.selections[key]];
        }
        if (!hit) hit = self.feeBridge[(self.config.set_handle || '') + '::' + key + '::' + self.selections[key]];
        if (hit && hit.v) carrierIds.push(hit.v);
        else console.error('[bg-options] no fee-carrier mapping for', key, self.selections[key]);
      });
    }
    if (carrierIds.length) addHidden('_bg_fee_carriers', carrierIds.join(','));
    if (feeEntries.length) {
      addHidden('_bg_fees', JSON.stringify(feeEntries));
      addHidden('_bg_fee_summary', feeEntries.map(function (f) { return f.l + ' +$' + f.a; }).join(' · '));
      var feeTotal = feeEntries.reduce(function (s, f) { return s + (parseFloat(f.a) || 0); }, 0);
      addHidden('_bg_fee_total', feeTotal.toFixed(2)); // cart breakdown block + packing slips
    }
  };

  // ---- Product form + ATC guard ---------------------------------------------
  BgOptionsEngine.prototype.bindProductForm = function () {
    var self = this;
    // Enterprise renders SEVERAL /cart/add forms (instalments widget first in
    // DOM, then card quick-adds) — the buyable PDP form is .js-product-form-main.
    // Binding to the wrong form silently drops every _bg_* property (found
    // 2026-07-17 during the first cart E2E).
    this.productForm =
      document.querySelector('form.js-product-form-main') ||
      this.root.closest('form[action^="/cart/add"]') ||
      document.querySelector('product-form form[action^="/cart/add"]:not(.js-instalments-form), form[action^="/cart/add"]:not(.js-instalments-form)');
    if (!this.productForm) return;

    // Native Shopify cart uploads: file inputs live in the panel (outside the
    // form element), so associate them via the form attribute — FormData then
    // carries the file to /cart/add and Shopify hosts it; the ORDER line shows
    // the property as a direct download link (same engraver workflow as Avis,
    // minus the vendor CDN).
    if (!this.productForm.getAttribute('id')) this.productForm.setAttribute('id', 'bg-main-product-form');
    var formId = this.productForm.getAttribute('id');
    this.root.querySelectorAll('[data-bg-file-input]').forEach(function (inp) {
      inp.setAttribute('form', formId);
    });

    this.productForm.addEventListener('submit', function (e) {
      var up = self.uploadFlowUnsatisfied();
      if (up) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.focusOffending(up);
        alert('Please attach your artwork file before continuing -- the logo upload option is selected but no file has been added.');
        return;
      }
      // #44210 part 2: a file IS attached, so the artwork must survive. Shopify's AJAX
      // cart silently drops file uploads, so stop the theme's AJAX handler from running
      // and let the browser's DEFAULT native multipart submit carry the file instead.
      // (stopImmediatePropagation WITHOUT preventDefault == "no AJAX, yes native post".)
      if (self.uploadFlowEngaged()) {
        e.stopImmediatePropagation();
        return;
      }
      var result = self.validateRequiredSelections();
      if (!result.valid) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.focusOffending(result);
        return;
      }
      // A2 (row 37, 2026-07-30): uploadPendingFiles() removed -- it called a
      // never-deployed app-proxy route (/apps/bg-options/upload, HTTP 404 live,
      // shopify.app.toml has no [app_proxy] block). The customer's file already
      // rides to /cart/add on the native <input type="file"> above (see the
      // form-association block at the top of this method); Shopify hosts it and
      // the order line shows a direct download link. No fallback call needed here.
    }, true); // capture phase — must win over the theme's own submit handler timing concerns
  };

  // ---- Express-wallet guard (Buy Now / Shop Pay / Apple Pay) — SPEC §7 ------
  BgOptionsEngine.prototype.bindExpressWallet = function () {
    var self = this;
    // FEE-GUARD v2 (2026-07-22): (a) never bind to the Customize trigger (it reuses
    // .shopify-payment-button for styling) regardless of DOM order; (b) the fee
    // check must NOT depend on the panel being open -- selections persist after
    // collapse (collapse != clear) and the hidden _bg_* inputs stay in the form,
    // so a fee-bearing config with the panel collapsed could express-checkout
    // WITHOUT its fee carrier lines (undercharge).
    var container = document.querySelector('.product-info__dynamic-checkout-button .shopify-payment-button') ||
      document.querySelector('.shopify-payment-button:not([data-bg-options-trigger]):not(.bg-options-trigger)');
    if (!container) return;
    container.addEventListener('click', function (e) {
      var up = self.uploadFlowUnsatisfied();
      if (up) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.focusOffending(up);
        alert('Please attach your artwork file before continuing -- the logo upload option is selected but no file has been added.');
        return;
      }
      // An express wallet cannot carry an uploaded file at all -- not even when one is
      // attached. Blocking here is what stops a paid engraving from shipping with no art.
      if (self.uploadFlowEngaged()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        alert('Your order includes an uploaded design. Please use \'Add to Cart\' so your artwork uploads with the order -- express checkout cannot carry a file.');
        return;
      }
      var result = self.validateRequiredSelections();
      if (!result.valid) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.focusOffending(result);
        return;
      }
      // LAUNCH fee bridge: express wallets buy the form product ONLY -- carrier
      // lines never ride along, so a fee-bearing config through Buy Now would
      // UNDERCHARGE. Route those through Add to Cart. Purchases with no
      // fee-bearing selections stay untouched.
      var hasFees = Object.keys(self.feeState).some(function (k) {
        var el = self.fieldEls[k];
        return !(el && el.hidden) && self.feeState[k] && self.feeState[k].amount;
      });
      if (hasFees) {
        e.preventDefault();
        e.stopImmediatePropagation();
        alert('Customized bottles are added through Add to Cart so your customization is included — please use the Add to Cart button.');
      }
      // if valid and no fees: let Shopify's native dynamic-checkout flow proceed unimpeded.
    }, true); // capture phase, same pattern as avis-atc-popup-decoupled
  };

  // ---- Display-only "from" prices (RD 2026-07-18: every touched swatch shows a price) ----
  // Zero-fee swatches whose real cost lives DOWNSTREAM get a computed pill: design-type
  // tiles show the MIN fee of their flow's finish step; product-picker tiles (glassware)
  // show the MIN fee of the item's dependent engraving group (Avis prices glassware at $0
  // and bakes the item cost into its engraving fees — this mirrors that, display-only,
  // never enters feeState, never double-charges).
  BgOptionsEngine.prototype.stampFromPrices = function () {
    var self = this;
    function minFee(opts) {
      var m = 0;
      opts.forEach(function (o) {
        (o.choices || []).forEach(function (c) {
          var a = (c.fee_rule && parseFloat(c.fee_rule.amount)) || 0;
          if (a > 0 && (m === 0 || a < m)) m = a;
        });
      });
      return m;
    }
    var FLOW_RE = {
      'personal-message': /text-finish/,
      'edge-engraving': /text-finish|(^|-)text$/,
      'etch-engraving': /text-finish|(^|-)text$/,
      'logo-upload': /logo-finish|logo/,
      'custom-design': /design-finish|custom-design/
    };
    this.config.groups.forEach(function (g) {
      g.options.forEach(function (o) {
        var el = self.fieldEls[o.key];
        if (!el || o.input_type !== 'swatch_image') return;
        (o.choices || []).forEach(function (c) {
          if (((c.fee_rule && parseFloat(c.fee_rule.amount)) || 0) > 0) return; // real fee renders already
          var from = 0;
          if (/-design-type$/.test(o.key)) {
            var re = FLOW_RE[c.value];
            if (re) {
              var tokn = o.key.split('-')[0];
              from = minFee(g.options.filter(function (s) { return s !== o && s.key.indexOf(tokn) === 0 && re.test(s.key); }));
            }
          } else if (/-custom-design$/.test(o.key)) {
            // v2 (2026-07-24): crest tiles — price lives on the NEXT step (design
            // finish); stamp the flow's minimum so crests are never price-blind.
            from = minFee(g.options.filter(function (s) { return s !== o && /-design-finish$|design-finish/.test(s.key); }));
          } else {
            // product picker tile: LONGEST-token DEP match wins (two-glencairn beats glencairn)
            var blob = String(c.value).toLowerCase();
            var best = null, bestLen = 0;
            Object.keys(DEP_GROUP_TOKENS).forEach(function (gk) {
              var t = DEP_GROUP_TOKENS[gk];
              if (blob.indexOf(t) !== -1 && t.length > bestLen) { best = gk; bestLen = t.length; }
            });
            if (best) {
              self.config.groups.forEach(function (g2) { if (g2.key === best) from = minFee(g2.options); });
            }
          }
          if (!(from > 0)) return;
          var tile = el.querySelector('[data-bg-tile][data-value="' + c.value + '"]');
          if (!tile) return;
          var tag = tile.querySelector('.tag');
          if (!tag) { tag = document.createElement('span'); tag.className = 'tag'; tile.appendChild(tag); }
          if (!tag.textContent.trim()) tag.textContent = 'from +$' + from.toFixed(2); // v2: honest "from" semantics on computed min-price pills
        });
      });
    });
  };

  function boot() {
    document.querySelectorAll('[data-bg-options-root]').forEach(function (root) {
      new BgOptionsEngine(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
